import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { formatRelativeDate, formatSalary, isDateOlderThanAnDateToRemove, regionName, stripHtml } from '../../utils/utils';
import { addFavouritePost, deletePost, removeFavouritePost } from '../../services/PostService';
import { deleteSeekingPost } from '../../services/SeekingPostService';
import { notificationType } from '../../common/lang';
import { notifyUser } from '../../services/NotificationService';
import { fetchNotifications } from '../../redux/slices/notificationsSlice';
import { getLoggedInUserId } from '../../services/AuthService';
import NationFlag from '../common/NationFlag';
import { brand } from '../../theme';
import { detailsPathFor } from '../../common/site';

export const detailsPath = (post, seek) => detailsPathFor(post.id, seek);

export const isExternalPost = (post) => post?.source === 'ADZUNA' || Boolean(post?.redirectUrl);

export function getPostLocation(post, countries, lang, t) {
  if (!post) return '';
  if (post.remote) return t('postDetails.remote');
  const city = (countries || []).find((c) => String(c.countryId) === String(post.place))?.countryName;
  const nation = post.nationTag ? regionName(post.nationTag, lang, '') : (isNaN(post.nation) ? post.nation : '');
  return [city, nation].filter(Boolean).join(', ');
}

/**
 * Job card.
 * mode: "browse" (search results), "favourites" or "owner" (your posts: edit / delete / stats).
 */
export default function PostComponent({ post, seek = false, mode = 'browse', countries = [], onDeleted, onFavouriteChange, jobCategory }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'en';
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [favBusy, setFavBusy] = useState(false);

  const userId = getLoggedInUserId();
  const isLoggedIn = Boolean(userId);
  const isOwner = mode === 'owner';
  const isMine = isLoggedIn && String(post.userId) === String(userId);
  const external = isExternalPost(post);
  const isFav = (post.favouriteUsers || []).map(String).includes(String(userId));
  const isNew = !isDateOlderThanAnDateToRemove(post.insertData, 7);
  const locationLabel = getPostLocation(post, countries, lang, t);
  const salary = !seek ? formatSalary(post.salary, post.typeSalary, lang) : '';
  const preview = stripHtml(post.description).slice(0, 180);
  const returnUrl = `${location.pathname}${location.search}`;
  const detailsState = { post, seek, switchChecked: seek, countryName: locationLabel, jobCategory, returnUrl };

  const refreshNotifications = () => dispatch(fetchNotifications({ userId, page: 1 }));

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await (seek ? deleteSeekingPost(post.id) : deletePost(post.id, userId));
      notifyUser(userId, seek ? notificationType.deleteSeekingJob : notificationType.deleteJob).then(refreshNotifications);
      onDeleted && onDeleted(post);
      setConfirmOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  const toggleFavourite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) {
      navigate('/login', { state: { from: returnUrl } });
      return;
    }
    const next = !isFav;
    const payload = { userId: Number(userId), postId: post.id };
    setFavBusy(true);
    // Optimistic update: the heart reacts instantly, rolled back if the request fails.
    onFavouriteChange && onFavouriteChange(post.id, next);
    try {
      await (next ? addFavouritePost(payload) : removeFavouritePost(payload));
      notifyUser(userId, next ? notificationType.addFavourite : notificationType.deleteFavourite).then(refreshNotifications);
    } catch (error) {
      console.error(error);
      onFavouriteChange && onFavouriteChange(post.id, !next);
    } finally {
      setFavBusy(false);
    }
  };

  const editPost = (e) => {
    e.stopPropagation();
    navigate(`/edit-post/${post.id}/${Boolean(seek)}`);
  };

  const chips = [];
  if (post.remote) chips.push({ icon: <HomeWorkOutlinedIcon />, label: t('postDetails.remote'), color: '#059669', bg: '#ECFDF5' });
  else if (locationLabel) chips.push({ icon: <PlaceOutlinedIcon />, label: locationLabel });
  if (salary) chips.push({ icon: <PaymentsOutlinedIcon />, label: salary, color: '#B45309', bg: '#FFFBEB' });
  if (!seek && post.numberRoles > 1) chips.push({ icon: <GroupsOutlinedIcon />, label: t('jobs.openings', { count: post.numberRoles }) });
  if (post.contract) chips.push({ label: post.contract });
  if (post.workingTime) chips.push({ label: post.workingTime });

  const applyButton = () => {
    if (isOwner || isMine) return null;
    if (external) {
      return (
        <Button
          component="a"
          href={post.redirectUrl || post.webSite}
          target="_blank"
          rel="noopener noreferrer nofollow"
          onClick={(e) => e.stopPropagation()}
          variant="contained"
          size="small"
          endIcon={<LaunchRoundedIcon />}
          sx={{ backgroundImage: 'none', backgroundColor: brand.orange, boxShadow: 'none', '&:hover': { backgroundColor: '#EA580C' } }}
        >
          {t('jobs.applyOnAdzuna')}
        </Button>
      );
    }
    const target = `/candidate?id=${post.id}&type=${seek ? 'seek' : 'offer'}`;
    return (
      <Button
        component={RouterLink}
        to={isLoggedIn ? target : '/login'}
        state={isLoggedIn ? { post, switchChecked: seek } : { from: target }}
        onClick={(e) => e.stopPropagation()}
        variant="contained"
        size="small"
        endIcon={<SendRoundedIcon />}
      >
        {seek ? t('user.directContactTitle') : t('jobs.applyNow')}
      </Button>
    );
  };

  return (
    <>
      <Card
        sx={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform .25s ease, box-shadow .25s ease, border-color .25s ease',
          '@media (hover: hover)': {
            '&:hover': {
              transform: 'translateY(-3px)',
              borderColor: alpha(brand.primary, 0.35),
              boxShadow: `0 18px 40px -20px ${alpha(brand.primary, 0.45)}`,
            },
          },
        }}
      >
        <CardActionArea
          component={RouterLink}
          to={detailsPath(post, seek)}
          state={detailsState}
          sx={{ flexGrow: 1, alignItems: 'stretch', display: 'flex', flexDirection: 'column', p: { xs: 2, sm: 2.5 }, '& .MuiCardActionArea-focusHighlight': { opacity: 0 } }}
        >
          <Stack direction="row" spacing={1.75} alignItems="flex-start" sx={{ width: '100%' }}>
            <NationFlag tag={post.nationTag} remote={post.remote} size={46} />
            <Box sx={{ minWidth: 0, flex: 1, pr: mode === 'owner' || seek ? 0 : 5 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                {isNew && (
                  <Chip label={t('jobs.new')} size="small" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 800, color: '#FFF', background: brand.gradient }} />
                )}
                <Chip
                  label={post.source === 'ADZUNA' ? 'Adzuna' : 'PlaceYourJob'}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    color: post.source === 'ADZUNA' ? '#C2410C' : brand.slate,
                    backgroundColor: post.source === 'ADZUNA' ? '#FFF7ED' : '#F1F5F9',
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                  <ScheduleRoundedIcon sx={{ fontSize: 14 }} />
                  {formatRelativeDate(post.insertData, lang)}
                </Typography>
              </Stack>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontSize: { xs: '1.02rem', sm: '1.12rem' },
                  lineHeight: 1.3,
                  fontWeight: 800,
                  wordBreak: 'break-word',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {post.title}
              </Typography>
              {post.company && (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mt: 0.25 }} noWrap>
                  {post.company}
                </Typography>
              )}
            </Box>
          </Stack>

          {preview && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 1.5,
                display: '-webkit-box',
                WebkitLineClamp: { xs: 2, sm: 3 },
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                width: '100%',
              }}
            >
              {preview}
            </Typography>
          )}

          {chips.length > 0 && (
            <Stack direction="row" sx={{ mt: 1.75, flexWrap: 'wrap', gap: 0.75, width: '100%' }}>
              {chips.map((chip) => (
                <Chip
                  key={chip.label}
                  icon={chip.icon}
                  label={chip.label}
                  size="small"
                  sx={{
                    maxWidth: '100%',
                    color: chip.color || brand.slate,
                    backgroundColor: chip.bg || '#F1F5F9',
                    '& .MuiChip-icon': { color: 'inherit', fontSize: 16 },
                  }}
                />
              ))}
            </Stack>
          )}
        </CardActionArea>

        {/* Favourites only exist for job offers (seeking posts share ids with another table). */}
        {mode !== 'owner' && !seek && (
          <Tooltip title={isLoggedIn ? (isFav ? t('postDetails.removeFromFav') : t('postDetails.addToFav')) : t('jobs.loginToSave')}>
            <IconButton
              onClick={toggleFavourite}
              disabled={favBusy}
              aria-label={isFav ? t('postDetails.removeFromFav') : t('postDetails.addToFav')}
              aria-pressed={isFav}
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 12 },
                right: { xs: 8, sm: 12 },
                color: isFav ? '#E11D48' : brand.muted,
                backgroundColor: isFav ? '#FFF1F2' : 'transparent',
                '&:hover': { backgroundColor: '#FFF1F2', color: '#E11D48' },
              }}
            >
              {isFav ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
            </IconButton>
          </Tooltip>
        )}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, borderTop: `1px solid ${brand.border}`, gap: 1, flexWrap: 'wrap' }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ color: 'text.secondary' }}>
            {isOwner ? (
              <>
                <Tooltip title={t('jobs.views')}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{post.views || 0}</Typography>
                  </Stack>
                </Tooltip>
                <Tooltip title={t('jobs.postedOn')}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <SendRoundedIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{post.jobSubmissions || 0}</Typography>
                  </Stack>
                </Tooltip>
              </>
            ) : (
              !external && (
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {t('jobs.applicantsCount', { count: post.jobSubmissions || 0 })}
                </Typography>
              )
            )}
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {isOwner ? (
              <>
                <Tooltip title={t('common.edit')}>
                  <IconButton onClick={editPost} aria-label={t('common.edit')} sx={{ color: 'primary.main' }}>
                    <EditOutlinedIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('common.delete')}>
                  <IconButton color="error" onClick={() => setConfirmOpen(true)} aria-label={t('common.delete')}>
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              applyButton()
            )}
          </Stack>
        </Stack>
      </Card>

      <Dialog open={confirmOpen} onClose={() => !deleting && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{t('jobs.confirmDeletionTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('jobs.confirmDeleteSingleJob')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting} sx={{ color: 'text.secondary' }}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting} sx={{ backgroundImage: 'none' }}>
            {deleting ? <CircularProgress size={20} color="inherit" /> : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
